// frontend/src/lib/demo-identity.ts
//
// Who the product is talking to.
//
// The demo is driven from one shared Keycloak login, so `session.user.name`
// resolves to "Super Admin" and every screen that greets a person greeted
// "Super". CB is the person in the room:
//
//   "Okay, so first changes, we will hardcode, uh, good evening CB or good
//    morning CB, whatever it is." — Agree Demo Changes, 21 Sep.
//
// and, about everywhere else the session name surfaces:
//
//   "The only thing is wherever it is super admin, it has to be mine."
//
// One constant so the greeting on the first screen and Megan's closing line on
// a delivered launch cannot drift apart. Set it to '' to go back to the
// session's own name wherever it is used with a fallback.
//
// This is a demo-account patch, not the fix. The real fix is renaming the
// Keycloak user, which also corrects the header, the approver name on a
// decision and the audit trail's "decided by".

/** The first name every screen greets. */
export const DEMO_FIRST_NAME = 'Ciby'

/**
 * The name recorded against a decision in the governance trail.
 *
 * `RAMP_OPERATOR` used to be the role, "HR Service Delivery Specialist", on the
 * reasoning that "one honest role beats a display name that the demo login may
 * not carry." That reasoning no longer holds: the login is pinned here, and the
 * first real run on the new Auto account recorded the role against every
 * decision while Ciby was the person taking them. Set this to '' to go back to
 * the role.
 */
export const DEMO_FULL_NAME = 'Ciby Yohannan'

/**
 * Who is an admin by name rather than by Keycloak role.
 *
 * Every data route is gated on the token carrying `admin` or `user`, and the
 * realm behind a fresh deployment does not hand those out: a user made in the
 * console gets `default-roles-<realm>` and nothing else, signs in fine, and is
 * then answered 403 by every screen. Until whoever holds the realm assigns the
 * role, anyone signing in from the company domain is treated as `admin`.
 *
 * Only ever ADDS a role to a user who has none of `admin`, `user` or `pending`.
 * Entries are full addresses or `@domain`s, matched case-insensitively. The
 * runtime env `AUTHZ_IMPLICIT_ADMINS` overrides the list (empty switches it
 * off). The backend applies the same rule from the same default in
 * `app/core/config.py`; keep the two identical.
 */
export const DEMO_IMPLICIT_ADMINS = ['@supervity.ai']

/** The roles the application itself hands out. */
export const APP_ROLES = ['admin', 'user', 'pending']

/** True when `email` is on the implicit-admin list, by address or by domain. */
export function isImplicitAdmin(email: string | undefined | null): boolean {
  if (!email || !email.includes('@')) return false
  const who = email.trim().toLowerCase()
  const domain = who.slice(who.lastIndexOf('@'))
  const raw = process.env.AUTHZ_IMPLICIT_ADMINS ?? DEMO_IMPLICIT_ADMINS.join(',')
  const listed = raw
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
  return listed.includes(who) || listed.includes(domain)
}
